import type { ReactNode } from 'react';
import { Icon } from '../common';

// The Account tab shell — a view-body that stacks an optional toolbar and a
// .fin-panel card with a header (title + optional right-side content). The
// Activity and Heatmap tabs both render through it, so the house framing
// (account-tab-view › fin-panel › fin-panel-head) lives in one place.
export function TabPanel({
  title,
  right,
  toolbar,
  children,
}: {
  title: string;
  right?: ReactNode;
  toolbar?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="account-tab-view">
      {toolbar}
      <div className="fin-panel">
        <div className="fin-panel-head">
          <h3>{title}</h3>
          {right}
        </div>
        {children}
      </div>
    </div>
  );
}

// Small presentational pieces shared by the three Account tabs, so the same
// markup isn't repeated in People / Activity / Heatmap.

// The allowlist role as a pill — or an "off-list" tag when the actor has
// activity but is no longer on the allowlist (role == null). One definition for
// the People column, the feed rows and the heatmap row heads.
export function RoleBadge({ role }: { role: string | null }) {
  return role ? (
    <span className={'account-role-pill role-' + role}>{role}</span>
  ) : (
    <span className="off-list-tag" title="Has activity but is not on the allowlist">
      off-list
    </span>
  );
}

// The inline error banner (account-error) shared across the tabs.
export function AccountError({ message }: { message: string }) {
  return (
    <div className="account-error" role="alert">
      <Icon name="alert-triangle" size={15} />
      <span>{message}</span>
    </div>
  );
}

// The designed empty state (icon + title + microcopy) shared by the Activity and
// Heatmap tabs.
export function TabEmpty({ icon, title, children }: { icon: string; title: string; children: ReactNode }) {
  return (
    <div className="feed-empty">
      <span className="feed-empty-mark">
        <Icon name={icon} size={20} />
      </span>
      <div className="feed-empty-title">{title}</div>
      <div className="feed-empty-sub">{children}</div>
    </div>
  );
}
